
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, Edit, Eye, Loader2, AlertCircle, Archive, RefreshCw, Settings } from "lucide-react";
import { logActivity } from "@/utils/userUtils";
import { Order } from "@/hooks/useOrdersManagement/types";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { archiveOrder } from "@/utils/orderUtils";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/auth/AuthProvider";
import { useAdminAuth } from "@/hooks/useAdminAuth";

interface RecentOrdersProps {
  orders: Order[];
  onCreateFirstOrder: () => void;
  onEditOrder: (order: Order) => void;
  onViewProducts: (order: Order) => void;
  isLoading?: boolean;
  error?: any;
}

interface OrderWithStatus extends Order {
  isSubmitted?: boolean;
}

const OrderStatusBadge = ({ order }: { order: Order }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkSubmissionStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('products')
          .select('submit_status')
          .eq('order_id', order.id)
          .eq('submit_status', 'committed');
        
        if (error) throw error;
        setIsSubmitted(data && data.length > 0);
      } catch (error) {
        console.error('Error checking submission status:', error);
      }
    };

    checkSubmissionStatus();
  }, [order.id]);

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsArchiving(true);
    
    try {
      const result = await archiveOrder(order.id);
      if (result.success) {
        toast({
          title: "Order Archived",
          description: "Order has been moved to archived orders.",
        });
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: "Archive Failed",
        description: "Failed to archive order. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const getStatusConfig = () => {
    if (isSubmitted) {
      return {
        text: "Submitted",
        className: "bg-blue-100 text-blue-800",
        showArchive: true
      };
    }
    switch (order.status) {
      case "active":
        return {
          text: "Active",
          className: "bg-green-100 text-green-800",
          showArchive: false
        };
      case "completed":
        return {
          text: "Completed",
          className: "bg-gray-100 text-gray-800",
          showArchive: true
        };
      case "archived":
        return {
          text: "Archived",
          className: "bg-gray-100 text-gray-600",
          showArchive: false
        };
      default:
        return {
          text: order.status,
          className: "bg-gray-100 text-gray-800",
          showArchive: false
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="space-y-1">
      <p className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.className}`}>
        {statusConfig.text}
      </p>
      {statusConfig.showArchive && (
        <Button
          onClick={handleArchive}
          disabled={isArchiving}
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
        >
          {isArchiving ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Archive className="w-3 h-3" />
          )}
        </Button>
      )}
    </div>
  );
};

export const RecentOrders = ({ 
  orders, 
  onCreateFirstOrder, 
  onEditOrder, 
  onViewProducts, 
  isLoading = false,
  error 
}: RecentOrdersProps) => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();
  
  const isAdmin = isAdminAuthenticated && profile;
  const [isRefreshing, setIsRefreshing] = useState(false);

  console.log('🎯 RecentOrders render:', { 
    ordersLength: orders?.length || 0, 
    isLoading, 
    hasError: !!error,
    orders: orders?.map(o => ({ id: o.id, supplier: o.supplierName }))
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 Starting manual refresh of orders...');
      // Properly await the refetch operation
      await queryClient.refetchQueries({ 
        queryKey: ['orders'],
        type: 'active'
      });
      console.log('✅ Orders refresh completed');
    } catch (error) {
      console.error('❌ Failed to refresh orders:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Recent Orders</CardTitle>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="text-center py-6">
            <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-3" />
            <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading Orders</h3>
            <p className="text-sm text-red-500 mb-4">
              {error.message || "Unable to load orders. Please try refreshing the page."}
            </p>
            <Button onClick={() => window.location.reload()} variant="outline" size="sm">
              Refresh Page
            </Button>
          </div>
        ) : isLoading ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading orders...</span>
            </div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <div className="text-right space-y-2">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-5 w-12" />
                    <div className="flex gap-1">
                      <Skeleton className="h-7 w-8" />
                      <Skeleton className="h-7 w-8" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !orders || orders.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Orders Yet</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Your order history is empty. Start by checking in your first supplier delivery to begin tracking inventory.
            </p>
            <Button 
              onClick={() => {
                console.log('📝 Creating first order...');
                logActivity('Started first order creation');
                onCreateFirstOrder();
              }} 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Package className="w-5 h-5 mr-2" />
              Create Your First Order
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div 
                key={order.id} 
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={(e) => {
                  console.log('🎯 Order row clicked:', order);
                  e.preventDefault();
                  onViewProducts(order);
                }}
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-sm font-mono">{order.supplierName}</h4>
                  <p className="text-xs text-gray-600">#{order.id}</p>
                  <p className="text-xs text-gray-500">Delivery: {order.deliveryDate}</p>
                  {order.createdBy && (
                    <p className="text-xs text-blue-600">Added by: {order.createdBy}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium">{order.productCount} products</p>
                  <OrderStatusBadge order={order} />
                  <div className="flex gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      onClick={() => onEditOrder(order)}
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                      title={isAdmin ? "Manage Order" : "Edit Order"}
                    >
                      {isAdmin ? <Settings className="w-3 h-3" /> : <Edit className="w-3 h-3" />}
                    </Button>
                    <Button
                      onClick={(e) => {
                        console.log('🎯 View button clicked:', order);
                        e.preventDefault();
                        e.stopPropagation();
                        onViewProducts(order);
                      }}
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                    >
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
